<template>
    <div class="producto-grid">
        <div class="row q-col-gutter-md">
            <!-- Sidebar de filtros -->
            <div class="col-12 col-md-3">
                <!-- Toggle filtros en mobile -->
                <q-btn class="full-width q-mb-md lt-md" outline color="primary" icon="tune" label="Filtros"
                    @click="filterDrawer = true" />

                <!-- Filtros Desktop - Sticky con scroll independiente -->
                <div class="gt-sm filters-sidebar-wrapper">
                    <q-scroll-area class="filters-scroll-area">
                        <FiltersSidebar v-model:search="search" v-model:productosFiltro="productosFiltro"
                            v-model:filtro="filtro" :variables="variables" :es-filtro-panel="esFiltroPanel"
                            :active-filters-count="activeFiltersCount" :loading="loading" @update:filter="filterUpdate"
                            @clear-filters="clearFilters" />
                    </q-scroll-area>
                </div>

                <!-- Drawer de filtros Mobile -->
                <q-drawer v-model="filterDrawer" side="right" overlay elevated :width="320"
                    class="lt-md filters-drawer">
                    <q-scroll-area class="fit">
                        <div class="q-pa-md">
                            <div class="row items-center justify-between q-mb-md">
                                <span class="text-h6">Filtros</span>
                                <q-btn flat round dense icon="close" @click="filterDrawer = false" />
                            </div>
                            <FiltersSidebar v-model:search="search" v-model:productosFiltro="productosFiltro"
                                v-model:filtro="filtro" :variables="variables" :es-filtro-panel="esFiltroPanel"
                                :active-filters-count="activeFiltersCount" @update:filter="filterUpdate"
                                @clear-filters="clearFilters" />
                        </div>
                    </q-scroll-area>
                </q-drawer>
            </div>

            <!-- Grid de productos -->
            <div class="col-12 col-md-9">
                <!-- Toolbar superior -->
                <q-card class="q-mb-md producto-grid__toolbar">
                    <q-card-section class="row items-center q-py-md">
                        <div class="col-12 col-sm-6 q-mb-sm-none q-mb-xs">
                            <span class="text-body2 text-grey-7">
                                Mostrando {{ productos.length }} productos
                                <template v-if="activeFiltersCount > 0">
                                    ({{ activeFiltersCount }} filtros activos)
                                </template>
                            </span>
                        </div>
                        <div class="col-12 col-sm-6 row justify-end items-center q-gutter-sm">
                            <!-- Vista Grid/Lista -->
                            <q-btn-toggle v-model="viewMode" flat dense :options="[
                                { value: 'grid', icon: 'grid_view', slot: 'grid' },
                                { value: 'list', icon: 'view_list', slot: 'list' }
                            ]">
                                <template v-slot:grid>
                                    <q-tooltip>Vista Grid</q-tooltip>
                                </template>
                                <template v-slot:list>
                                    <q-tooltip>Vista Lista</q-tooltip>
                                </template>
                            </q-btn-toggle>

                            <!-- Ordenamiento -->
                            <q-select v-model="sortBy" :options="sortOptions" dense outlined emit-value map-options
                                style="min-width: 150px" @update:model-value="handleSortChange" />

                            <!-- Importar Excel -->
                            <q-btn flat dense round icon="mdi-microsoft-excel" color="green-7" @click="showExcelImporter = true">
                                <q-tooltip>Importar</q-tooltip>
                            </q-btn>
                        </div>
                    </q-card-section>
                </q-card>

                <!-- Chips de filtros activos -->
                <transition name="slide-down">
                    <div v-if="activeFiltersCount > 0" class="q-mb-md">
                        <q-chip v-for="(chip, index) in activeFilterChips" :key="index" removable color="primary"
                            text-color="white" @remove="removeFilter(chip)" class="q-mr-xs q-mb-xs">
                            {{ chip.label }}
                        </q-chip>
                    </div>
                </transition>

                <!-- Grid de productos con scroll -->
                <q-scroll-area
                    style="height: calc(100vh - 120px)"
                    class="producto-grid__scroll-area"
                >
                    <q-infinite-scroll @load="onLoad" :offset="50" :debounce="1000" >
                        <!-- Loading skeletons -->
                        <div v-if="loading && productos.length === 0" class="row q-col-gutter-md">
                            <div v-for="n in 12" :key="`skeleton-${n}`" :class="gridColClass">
                                <ProductoCardSkeleton />
                            </div>
                        </div>
                        <!-- Grid View -->
                        <transition-group v-else-if="viewMode === 'grid'" name="product-grid" tag="div"
                            class="row q-col-gutter-md q-mt-none">
                            <div v-for="p in productos" :key="p.id" :class="gridColClass" class="producto-grid__item">
                                <ProductoCard :descripcion="p.productoDesc" :titulo="p.productoID" :precio="p.precio"
                                    :id="p.id" :grupo="p.webGroupDesc" :imagenesPath="productosImagesPath"
                                    @add="productoAdd(p)" @quick-view="showQuickView(p)"
                                    @toggle-favorite="toggleFavorite(p)" />
                            </div>
                        </transition-group>

                        <!-- List View (alternativa) -->
                        <transition-group v-else name="product-list" tag="div" class="column q-gutter-md">
                            <ProductoListItem v-for="p in productos" :key="p.id" :descripcion="p.productoDesc"
                                :titulo="p.productoID" :precio="p.precio" :id="p.id" :grupo="p.webGroupDesc"
                                :imagenesPath="productosImagesPath" @add="productoAdd(p)" />
                        </transition-group>

                        <!-- Empty State -->
                        <div v-if="!loading && productos.length === 0" class="producto-grid__empty">
                            <q-icon name="search_off" size="80px" color="grey-5" />
                            <h3 class="text-h5 text-grey-7 q-mt-md">No se encontraron productos</h3>
                            <p class="text-body2 text-grey-6">
                                Intenta ajustar los filtros o realizar una búsqueda diferente
                            </p>
                            <q-btn outline color="primary" label="Limpiar filtros" icon="refresh" @click="clearFilters"
                                class="q-mt-md" />
                        </div>

                        <template v-slot:loading>
                            <div class="infinite-scroll-loading">
                                <q-spinner-dots color="primary" size="40px" />
                                <p class="text-body2 text-grey-7 q-mt-sm">Obteniendo más productos...</p>
                            </div>
                        </template>
                    </q-infinite-scroll>

                    <!-- Indicador de final de resultados -->
                    <div v-if="!loading && productos.length > 0 && !hasMoreProducts" class="row q-py-md justify-center">
                        <q-chip color="grey-3" text-color="grey-7" icon="check_circle">
                            Has visto todos los productos
                        </q-chip>
                    </div>
                </q-scroll-area>
            </div>
        </div>
        <q-page-sticky position="bottom-right" :offset="[18, 18]" v-if="!isSavedPresup">
            <q-btn v-if="cartStore.itemTotal > 0" fab icon="shopping_cart" color="primary" @click="cartStore.toggle()">
                <q-badge color="red" floating>{{ cartStore.itemTotal }}</q-badge>
            </q-btn>
        </q-page-sticky>
        <ExcelImporter v-model="showExcelImporter" />
        <q-dialog v-model="dialog" persistent :maximized="maximizedToggle">
            <component :is="componentMapping[tipoProducto]" :oper="oper" :producto="producto" :formulario="formulario"
                :is-saved-presup="props.isSavedPresup" :presup-id="props.presupId" @added="onItemAdded"
                @updated="onItemUpdated"
                @maximized-toggle="maximizedToggle = !maximizedToggle">
            </component>
        </q-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive } from "vue";
import { useCartStore } from "@/stores/useCartStore";
import { usePage } from "@inertiajs/vue3";
import ExcelImporter from "@/pages/Presupuestos/components/ExcelImporter.vue";
import { useQuasar } from "quasar";
import CodigoService from "@/services/CodigoService";
import componentMapping from "@/constants/componentMapping.js";
import FiltersSidebar from "@/pages/Productos/components/FiltersSidebar.vue";
import PrecioListaService from "@/services/PrecioListaService";
import ProductoCard from "@/pages/Productos/components/ProductoCard.vue";
import ProductoCardSkeleton from "@/pages/Productos/components/ProductoCardSkeleton.vue";
import ProductoListItem from "@/pages/Productos/components/ProductoListItem.vue";

const page = usePage();
const productosImagesPath = page.props.productosImagesPath;
const scrollTargetRef = ref(null);
const maximizedToggle = ref(false);

const props = defineProps({
    isSavedPresup: {
        type: Boolean,
        default: false,
    },
    presupId: {
        type: Number,
        default: null,
    },
});

const emit = defineEmits(["item-added", "item-updated"]);

const $q = useQuasar();
const cartStore = useCartStore();
const productos = ref([]);
const producto = ref({});
const formulario = ref({});
const oper = ref("add");
const dialog = ref(false);
const loading = ref(false);
const search = ref("");
const currentPage = ref(1);
const viewMode = ref('grid'); // 'grid' o 'list'
const sortBy = ref('default');
const filterDrawer = ref(false);
const hasMoreProducts = ref(true);
const showExcelImporter = ref(false);

const filtro = reactive({
    tipo: [],
    extRev: [],
    extCol: [],
    extEsp: [],
    extTerm: [],
    intRev: [],
    intCol: [],
    intEsp: [],
    intTerm: [],
    densidad: [],
    nucleo: [],
    espesor: [],
});

const variables = ref([]);
const productosFiltro = ref([]);
const tipoProducto = ref("WPAN");

// Opciones de ordenamiento
const sortOptions = [
    { label: 'Relevancia', value: 'default' },
    { label: 'Precio: Menor a Mayor', value: 'price-asc' },
    { label: 'Precio: Mayor a Menor', value: 'price-desc' },
    { label: 'Nombre: A-Z', value: 'name-asc' },
    { label: 'Nombre: Z-A', value: 'name-desc' },
];

// Computed para clase de columnas responsive
const gridColClass = computed(() => {
    return 'col-12 col-sm-6 col-md-4 col-lg-3';
});

// Contador de filtros activos
const activeFiltersCount = computed(() => {
    let count = 0;
    if (productosFiltro.value.length > 0) count += productosFiltro.value.length;
    Object.keys(filtro).forEach(key => {
        if (Array.isArray(filtro[key]) && filtro[key].length > 0) {
            count += filtro[key].length;
        }
    });
    return count;
});

// Chips de filtros activos
const activeFilterChips = computed(() => {
    const chips = [];

    // Chips de tipo de producto
    productosFiltro.value.forEach(tipo => {
        chips.push({
            label: `Tipo: ${tipo}`,
            type: 'productosFiltro',
            value: tipo
        });
    });

    // Chips de otros filtros
    Object.keys(filtro).forEach(key => {
        if (Array.isArray(filtro[key])) {
            filtro[key].forEach(item => {
                chips.push({
                    label: `${formatFilterLabel(key)}: ${item.label || item}`,
                    type: key,
                    value: item
                });
            });
        }
    });

    return chips;
});

onMounted(async () => {
    loading.value = true;
    await getVariables();
    await getLista();
    loading.value = false;
});

const productoAdd = async (prod) => {
    tipoProducto.value = prod.webGroupID.trim();
    if (!tipoProducto.value || !componentMapping[tipoProducto.value]) {
        $q.notify({
            color: "negative",
            message: "Falta asignar tipo de producto web",
            icon: "warning",
        });
        return;
    }
    oper.value = "add";
    producto.value = prod;
    dialog.value = true;
};

const productoEdit = ({ producto: prod, form }) => {
    tipoProducto.value = prod.webGroupID.trim();
    if (!tipoProducto.value || !componentMapping[tipoProducto.value]) {
        $q.notify({
            color: "negative",
            message: "Falta asignar tipo de producto web",
            icon: "warning",
        });
        return;
    }
    oper.value = "edit";
    producto.value = prod;
    formulario.value = form;
    dialog.value = true;
};

const showQuickView = (prod) => {
    // Implementar vista rápida
    console.log('Quick view:', prod);
    // Aquí puedes abrir un modal con detalles rápidos del producto
};

const toggleFavorite = (prod) => {
    // Implementar favoritos
    console.log('Toggle favorite:', prod);
    $q.notify({
        message: 'Producto agregado a favoritos',
        icon: 'favorite',
        color: 'positive',
        position: 'top'
    });
};

cartStore.$onAction(({ name, after }) => {
    after(() => {
        if (name === "itemEdit") {
            productoEdit(cartStore.getItemToEdit);
        }
    });
});

const onLoad = async (index, done) => {
    if (!hasMoreProducts.value) {
        done();
        return;
    }

    currentPage.value++;
    await getLista();
    done();
};

const filterUpdate = async () => {
    loading.value = true;
    currentPage.value = 1;
    productos.value = [];
    hasMoreProducts.value = true;
    await getLista();
};

const clearFilters = () => {
    productosFiltro.value = [];
    Object.keys(filtro).forEach(key => {
        filtro[key] = [];
    });
    search.value = '';
    filterUpdate();
};

const removeFilter = (chip) => {
    if (chip.type === 'productosFiltro') {
        const index = productosFiltro.value.indexOf(chip.value);
        if (index > -1) {
            productosFiltro.value.splice(index, 1);
        }
    } else {
        const index = filtro[chip.type].findIndex(item =>
            (item.value || item) === (chip.value.value || chip.value)
        );
        if (index > -1) {
            filtro[chip.type].splice(index, 1);
        }
    }
    filterUpdate();
};

const handleSortChange = () => {
    // Implementar ordenamiento
    const sorted = [...productos.value];

    switch (sortBy.value) {
        case 'price-asc':
            sorted.sort((a, b) => parseFloat(a.precio) - parseFloat(b.precio));
            break;
        case 'price-desc':
            sorted.sort((a, b) => parseFloat(b.precio) - parseFloat(a.precio));
            break;
        case 'name-asc':
            sorted.sort((a, b) => a.productoID.localeCompare(b.productoID));
            break;
        case 'name-desc':
            sorted.sort((a, b) => b.productoID.localeCompare(a.productoID));
            break;
    }

    productos.value = sorted;
};

const formatFilterLabel = (key) => {
    const labels = {
        tipo: 'Tipo',
        extRev: 'Ext. Rev.',
        extCol: 'Ext. Color',
        extEsp: 'Ext. Espesor',
        extTerm: 'Ext. Term.',
        intRev: 'Int. Rev.',
        intCol: 'Int. Color',
        intEsp: 'Int. Espesor',
        intTerm: 'Int. Term.',
        densidad: 'Densidad',
        nucleo: 'Núcleo',
        espesor: 'Espesor'
    };
    return labels[key] || key;
};
const rowsPerPage = 50;
const getLista = async () => {
    const filter = {
        productoTipo: productosFiltro.value,
        tipo: filtro.tipo.map((x) => x.value),
        extRev: filtro.extRev.map((x) => x.value),
        extCol: filtro.extCol.map((x) => x.value),
        extEsp: filtro.extEsp.map((x) => x.value),
        extTerm: filtro.extTerm.map((x) => x.value),
        intRev: filtro.intRev.map((x) => x.value),
        intCol: filtro.intCol.map((x) => x.value),
        intEsp: filtro.intEsp.map((x) => x.value),
        intTerm: filtro.intTerm.map((x) => x.value),
        densidad: filtro.densidad.map((x) => x.value),
        nucleo: filtro.nucleo.map((x) => x.value),
        espesor: filtro.espesor.map((x) => x.value),
    };
    try {
        const result = await PrecioListaService.getActiveListItems(
            filter,
            search.value,
            currentPage.value,
            rowsPerPage
        );

        // Si recibimos menos productos que el límite, no hay más
        if (result.data.length < rowsPerPage) {
            hasMoreProducts.value = false;
        }

        productos.value = [...productos.value, ...result.data];
    } catch (error) {
        console.log(error);
    }
    loading.value = false;
};

const getVariables = async () => {
    variables.value = await CodigoService.variables();
};

const esFiltroPanel = computed(() => productosFiltro.value.includes("WPAN"));

const onItemAdded = () => emit("item-added");

const onItemUpdated = () => emit("item-updated");
</script>

<style lang="scss" scoped>
.producto-grid {
    &__toolbar {
        border-radius: 12px;
        border: 1px solid rgba(0, 0, 0, 0.08);
    }

    &__scroll-area {
        border-radius: 12px;
    }

    &__item {
        animation: fadeInUp 0.4s ease-out backwards;
    }

    &__empty {
        min-height: 400px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 48px 24px;
    }
}

// Loading state para infinite scroll
.infinite-scroll-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
    text-align: center;

    p {
        margin: 0;
        font-weight: 500;
        letter-spacing: 0.3px;
    }
}

// Sidebar de filtros fijo con scroll independiente
.filters-sidebar-wrapper {
    position: sticky;
    top: 20px;
    height: 95vh;
}

.filters-scroll-area {
    height: 100%;

    :deep(.q-scrollarea__content) {
        padding-right: 8px;
    }

    :deep(.q-scrollarea__thumb) {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        width: 6px;
        opacity: 0.8;

        &:hover {
            background: rgba(0, 0, 0, 0.3);
        }
    }
}

// Fix z-index para el drawer de filtros
:deep(.filters-drawer) {
    z-index: 3000 !important;
}

// Animaciones de entrada de productos
.product-grid-enter-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-grid-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-grid-enter-from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
}

.product-grid-leave-to {
    opacity: 0;
    transform: scale(0.95);
}

// Animación de lista
.product-list-enter-active {
    transition: all 0.3s ease-out;
}

.product-list-leave-active {
    transition: all 0.2s ease-in;
}

.product-list-enter-from {
    opacity: 0;
    transform: translateX(-20px);
}

.product-list-leave-to {
    opacity: 0;
    transform: translateX(20px);
}

// Slide down para chips
.slide-down-enter-active {
    transition: all 0.3s ease-out;
}

.slide-down-leave-active {
    transition: all 0.2s ease-in;
}

.slide-down-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

// Keyframe para fadeInUp
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Stagger animation para items del grid
@for $i from 1 through 20 {
    .producto-grid__item:nth-child(#{$i}) {
        animation-delay: #{$i * 0.05}s;
    }
}

// Responsive
@media (max-width: 768px) {
    .producto-grid {
        &__scroll-area {
            height: calc(100vh - 320px) !important;
        }
    }
}
</style>
